import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Copy, CreditCard, ExternalLink, Gift, Loader2, Mail, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getReferral, openPortal, syncBilling, subscriptionSummary, formatDate, formatMoney, PLAN_LABEL, PLAN_PRICE } from '../../lib/billingApi';
import { apiErrorMessage } from '../../lib/projectApi';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';

const heading = { fontFamily: 'Outfit, Inter, sans-serif' };

const STATUS_BADGE = {
  trialing: { label: 'Free trial', className: 'bg-primary/15 text-primary hover:bg-primary/15' },
  active: { label: 'Active', className: 'bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))] hover:bg-[hsl(var(--wp-context-green))]/15' },
  past_due: { label: 'Past due', className: 'bg-[hsl(var(--wp-context-amber))]/20 text-[hsl(var(--wp-context-amber))] hover:bg-[hsl(var(--wp-context-amber))]/20' },
  incomplete: { label: 'Payment pending', className: 'bg-[hsl(var(--wp-context-amber))]/20 text-[hsl(var(--wp-context-amber))] hover:bg-[hsl(var(--wp-context-amber))]/20' },
  canceled: { label: 'Ended', className: 'bg-muted text-muted-foreground hover:bg-muted' },
  unpaid: { label: 'Unpaid', className: 'bg-destructive/15 text-destructive hover:bg-destructive/15' },
  incomplete_expired: { label: 'Ended', className: 'bg-muted text-muted-foreground hover:bg-muted' },
};

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    return true;
  } catch { return false; }
};

/** Account ▸ Billing & plan: what the subscription is doing, with the Stripe portal for every change. */
const PlanPanel = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const billing = user?.billing || null;
  const summary = subscriptionSummary(billing);
  const badge = STATUS_BADGE[billing?.status];

  const portal = async () => {
    if (busy) return;
    setBusy('portal');
    try { const { url } = await openPortal(); window.location.assign(url); } catch (e) { setBusy(''); toast.error(await apiErrorMessage(e, 'The billing portal could not be opened.')); }
  };
  const sync = async () => {
    if (busy) return;
    setBusy('sync');
    try { await syncBilling(); await refresh(); toast.success('Subscription status refreshed', { id: 'billing' }); } catch (e) { toast.error(await apiErrorMessage(e, 'Stripe could not be reached.')); } finally { setBusy(''); }
  };

  return (
    <div className="space-y-4" data-testid="billing-plan-panel">
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground" data-testid="billing-summary-title">{summary.title}</div>
            <p className="mt-1 text-xs text-muted-foreground" data-testid="billing-summary-detail">{summary.detail}</p>
          </div>
          {badge && <Badge className={`shrink-0 ${badge.className}`} data-testid="billing-status-badge">{badge.label}</Badge>}
        </div>
        {billing?.plan && (
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs border-t border-border pt-3" data-testid="billing-facts">
            <dt className="text-muted-foreground">Plan</dt><dd className="text-foreground text-right">{PLAN_LABEL[billing.plan] || billing.plan} · {PLAN_PRICE[billing.plan] || ''}</dd>
            {billing.status === 'trialing' && billing.trial_end && <><dt className="text-muted-foreground">Trial ends</dt><dd className="text-foreground text-right">{formatDate(billing.trial_end)}</dd></>}
            {billing.current_period_end && billing.status !== 'trialing' && <><dt className="text-muted-foreground">{billing.cancel_at_period_end ? 'Access until' : 'Next bill'}</dt><dd className="text-foreground text-right">{formatDate(billing.current_period_end)}</dd></>}
          </dl>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {summary.tone === 'off' || summary.tone === 'none' ? (
          <Button type="button" className="h-10" onClick={() => navigate('/subscribe')} data-testid="billing-choose-plan"><CreditCard className="h-4 w-4 mr-1.5" aria-hidden="true" />Choose a plan</Button>
        ) : (
          <Button type="button" className="h-10" onClick={portal} disabled={!!busy || !billing} data-testid="billing-open-portal">{busy === 'portal' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" /> : <ExternalLink className="h-4 w-4 mr-1.5" aria-hidden="true" />}Manage in Stripe</Button>
        )}
        {billing && (summary.tone === 'off' || summary.tone === 'none') && <Button type="button" variant="outline" className="h-10" onClick={portal} disabled={!!busy} data-testid="billing-open-portal"><ExternalLink className="h-4 w-4 mr-1.5" aria-hidden="true" />Invoices &amp; card</Button>}
        <Button type="button" variant="outline" className="h-10" onClick={sync} disabled={!!busy || !billing} data-testid="billing-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy === 'sync' ? 'animate-spin' : ''}`} aria-hidden="true" />Refresh</Button>
      </div>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>Payments are handled by Stripe — WannaPublish never sees your card number. In the Stripe portal you can switch between monthly and annual, update your card, download invoices or cancel (you keep access until the end of what you have paid for).</span></p>
    </div>
  );
};

/** Account ▸ Refer a friend: the invite link, the rules, and how the friends are doing. */
const ReferPanel = ({ active }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    setError('');
    try { setData(await getReferral()); } catch (e) { setError(await apiErrorMessage(e, 'Your referral details could not be loaded.')); }
  }, []);
  useEffect(() => { if (active && !data) load(); }, [active, data, load]);

  const link = data ? `${window.location.origin}${data.link_path}` : '';
  const copy = async (what, text) => {
    if (await copyText(text)) { setCopied(what); toast.success(what === 'link' ? 'Invite link copied' : 'Code copied', { id: 'referral-copy' }); setTimeout(() => setCopied(''), 1800); } else toast.error('Could not copy — select the text and copy it yourself.');
  };
  const mailto = data ? `mailto:?subject=${encodeURIComponent('Try WannaPublish — your first month is free')}&body=${encodeURIComponent(`${user?.name ? `${user.name.split(' ')[0]} thought` : 'A friend thought'} you would like WannaPublish — desktop publishing in your browser. Join with this link and your first month is free (or $5 off the annual plan):\n\n${link}\n\nYour code: ${data.code}`)}` : '#';

  return (
    <div className="space-y-4" data-testid="billing-refer-panel">
      <p className="text-sm text-foreground">
        <span className="font-semibold">Give a month, get a month.</span> Friends who join through your link get <span className="font-medium">their first month free</span> (or <span className="font-medium">$5 off</span> the annual plan). When they pay their first bill you get <span className="font-medium">a free month</span> on the monthly plan or a <span className="font-medium">$5 credit</span> on annual — applied to your next invoice automatically.
      </p>
      {user?.referral?.referred_by && (
        <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="refer-you-were-referred"><Gift className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" aria-hidden="true" /><span>You joined through a friend’s invitation — your own discount is applied at checkout, and your friend is thanked once your first bill is paid.</span></p>
      )}
      {error ? (
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm flex flex-wrap items-center gap-3" data-testid="refer-error"><span className="flex-1">{error}</span><Button type="button" variant="outline" size="sm" onClick={load} data-testid="refer-retry">Try again</Button></div>
      ) : !data ? (
        <div className="space-y-3" data-testid="refer-loading"><Skeleton className="h-[92px] rounded-lg" /><Skeleton className="h-[64px] rounded-lg" /></div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-muted/30 p-3 sm:p-4 space-y-3">
            <div>
              <Label htmlFor="refer-link" className="text-xs">Your invite link</Label>
              <div className="mt-1.5 flex gap-2">
                <Input id="refer-link" readOnly value={link} onFocus={(e) => e.target.select()} className="h-10 font-mono text-xs bg-background" data-testid="refer-link-input" />
                <Button type="button" className="h-10 shrink-0" onClick={() => copy('link', link)} data-testid="refer-copy-link">{copied === 'link' ? <Check className="h-4 w-4 mr-1.5" aria-hidden="true" /> : <Copy className="h-4 w-4 mr-1.5" aria-hidden="true" />}{copied === 'link' ? 'Copied' : 'Copy'}</Button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
              <span>Code <code className="ml-1 font-mono text-sm font-semibold tracking-[0.15em] text-foreground" data-testid="refer-code">{data.code}</code></span>
              <button type="button" onClick={() => copy('code', data.code)} className="inline-flex items-center gap-1 rounded-sm underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="refer-copy-code">{copied === 'code' ? <Check className="h-3 w-3" aria-hidden="true" /> : <Copy className="h-3 w-3" aria-hidden="true" />}copy code</button>
              <a href={mailto} className="inline-flex items-center gap-1 rounded-sm underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="refer-share-email"><Mail className="h-3 w-3" aria-hidden="true" />share by email</a>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2" data-testid="refer-stats">
            {[
              { k: 'joined', label: 'Friends joined', value: data.friends.length },
              { k: 'rewards', label: 'Rewards earned', value: data.rewards_count },
              { k: 'credit', label: 'Credited to you', value: formatMoney(data.rewards_cents || 0) },
            ].map((s) => (
              <div key={s.k} className="rounded-lg border border-border bg-card px-3 py-2.5" data-testid={`refer-stat-${s.k}`}>
                <div className="text-lg font-semibold tabular-nums" style={heading}>{s.value}</div>
                <div className="text-[11px] text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
          {data.friends.length === 0 ? (
            <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="refer-empty"><Users className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" /><span>No one has joined through your link yet. Share it with a friend who makes flyers, menus or newsletters — the reward lands once they pay their first bill.</span></p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border" data-testid="refer-friends">
              {data.friends.map((f, i) => (
                <li key={`${f.created_at}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid="refer-friend-row">
                  <span className="text-muted-foreground">Friend joined {formatDate(f.created_at)}</span>
                  {f.status === 'rewarded'
                    ? <Badge className="bg-[hsl(var(--wp-context-green))]/15 text-[hsl(var(--wp-context-green))] hover:bg-[hsl(var(--wp-context-green))]/15">Rewarded {f.reward_cents ? formatMoney(f.reward_cents) : ''} · {formatDate(f.rewarded_at)}</Badge>
                    : <Badge variant="outline" className="text-muted-foreground">Signed up · reward after first paid bill</Badge>}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Account ▸ Billing & plan / Refer a friend — one dialog, two tabs. `tab` ('plan' | 'refer') picks the one that opens.
 */
export const BillingDialog = ({ open, onOpenChange, tab = 'plan', onTabChange }) => {
  const [current, setCurrent] = useState(tab);
  useEffect(() => { if (open) setCurrent(tab); }, [open, tab]);
  const change = (v) => { setCurrent(v); onTabChange?.(v); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] bg-card" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle style={heading}>{current === 'refer' ? 'Refer a friend' : 'Billing & plan'}</DialogTitle>
          <DialogDescription>{current === 'refer' ? 'Share WannaPublish and you both get something back.' : 'Your subscription, managed securely through Stripe.'}</DialogDescription>
        </DialogHeader>
        <Tabs value={current} onValueChange={change}>
          <TabsList className="grid w-full grid-cols-2 h-10">
            <TabsTrigger value="plan" className="text-sm" data-testid="billing-tab-plan"><CreditCard className="h-4 w-4 mr-1.5" aria-hidden="true" />Billing &amp; plan</TabsTrigger>
            <TabsTrigger value="refer" className="text-sm" data-testid="billing-tab-refer"><Gift className="h-4 w-4 mr-1.5" aria-hidden="true" />Refer a friend</TabsTrigger>
          </TabsList>
          <TabsContent value="plan" className="mt-4"><PlanPanel /></TabsContent>
          <TabsContent value="refer" className="mt-4"><ReferPanel active={current === 'refer'} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
